import React, { useState, useEffect } from 'react';
import pigeonIcon from '../assets/icons/pigeon_icon.png';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip
} from '@mui/material';
import {
  Male as MaleIcon,
  Female as FemaleIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
  TipsAndUpdates as TipsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DatabaseService from '../services/database';
import { getBirdListLabels } from '../utils/birdSearch';
import { sexAvatarSx } from '../utils/sexStyle';

// Soft tint + solid foreground per stat accent.
const TINTS = {
  primary: { bg: 'rgba(44,90,160,0.10)', fg: '#2c5aa0' },
  info: { bg: 'rgba(47,127,209,0.11)', fg: '#2f7fd1' },
  secondary: { bg: 'rgba(207,61,129,0.11)', fg: '#cf3d81' },
  success: { bg: 'rgba(46,158,107,0.13)', fg: '#2e9e6b' }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalBirds: 0,
    maleCount: 0,
    femaleCount: 0,
    recentBirds: 0
  });
  const [recentBirds, setRecentBirds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsData, birdsData] = await Promise.all([
        DatabaseService.getStats(),
        DatabaseService.getRecentBirds(5)
      ]);
      
      setStats(statsData);
      setRecentBirds(birdsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color = 'primary' }) => {
    const tint = TINTS[color] || TINTS.primary;
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box>
              <Typography variant="overline" color="text.secondary">
                {title}
              </Typography>
              <Typography
                variant="h3"
                component="div"
                sx={{ mt: 0.75, fontWeight: 750, letterSpacing: '-0.02em', color: 'text.primary', lineHeight: 1 }}
              >
                {value}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 48, height: 48, borderRadius: 3, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: tint.bg, color: tint.fg,
                '& svg': { fontSize: 26 }
              }}
            >
              {icon}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4} gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            An overview of your loft at a glance.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/birds/add')}
          size="large"
        >
          Add New Bird
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Birds"
            value={stats.totalBirds}
            icon={<img src={pigeonIcon} alt="" style={{ width: 26, height: 26 }} />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Males"
            value={stats.maleCount}
            icon={<MaleIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Females"
            value={stats.femaleCount}
            icon={<FemaleIcon />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Added This Month"
            value={stats.recentBirds}
            icon={<TrendingUpIcon />}
            color="success"
          />
        </Grid>
      </Grid>

      {/* Recent Birds and Quick Actions */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">
                  Recently Added Birds
                </Typography>
                {recentBirds.length > 0 && (
                  <Button
                    size="small"
                    endIcon={<ChevronRightIcon />}
                    onClick={() => navigate('/birds')}
                    sx={{ color: 'text.secondary' }}
                  >
                    View all
                  </Button>
                )}
              </Box>
              {recentBirds.length === 0 ? (
                <Box textAlign="center" py={5}>
                  <Typography color="text.secondary">
                    No birds added yet. Start by adding your first bird!
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/birds/add')}
                    sx={{ mt: 2 }}
                  >
                    Add First Bird
                  </Button>
                </Box>
              ) : (
                <List sx={{ mx: -1 }}>
                  {recentBirds.map((bird) => {
                    const { displayName, ringLabel } = getBirdListLabels(bird);
                    return (
                    <ListItem
                      key={bird.id}
                      button
                      onClick={() => navigate(`/birds/${bird.id}`)}
                      sx={{
                        borderRadius: 2.5,
                        px: 1.5,
                        py: 1.25,
                        '& .dash-chevron': { opacity: 0, transition: 'opacity .15s' },
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:hover .dash-chevron': { opacity: 1 }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={sexAvatarSx(bird.sex)}>
                          {bird.sex === 'male' ? <MaleIcon /> : <FemaleIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {displayName}
                            </Typography>
                            {ringLabel && (
                              <Chip
                                label={ringLabel}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          [bird.breed, bird.color, bird.year].filter(Boolean).join(' • ') || 'No additional details'
                        }
                        secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary', mt: 0.25 }}
                      />
                      <ChevronRightIcon className="dash-chevron" sx={{ color: 'text.disabled' }} />
                    </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          {/* Tips Card */}
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1.25} mb={2}>
                <Box
                  sx={{
                    width: 36, height: 36, borderRadius: 2.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: TINTS.success.bg, color: TINTS.success.fg
                  }}
                >
                  <TipsIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="h6">Tips</Typography>
              </Box>
              <Box display="flex" flexDirection="column" gap={1.75}>
                {[
                  'Add parent information when creating birds to build complete pedigrees.',
                  'Use clear, consistent naming conventions for easy searching.',
                  'Add photos to make your exported pedigrees look more professional.'
                ].map((tip, i) => (
                  <Box key={i} display="flex" gap={1.25}>
                    <Box sx={{ mt: '7px', width: 6, height: 6, borderRadius: '50%', flexShrink: 0, bgcolor: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">{tip}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
